import type { ApiErrorModel, ApplePayAccept, ApplePayTokensModel } from '@/common/models/api.models';
import type { SessionParamsModel } from '~/common/models/applePay.models.ts';
import type { ApplePayResult } from '@payments/interfaces/applePay';

export function useApplePay(
  successCallBack: () => void,
  onAction: () => void,
  requestPayerEmail = false,
) {
  const store = useKegelAnalyticStore();
  const API = useKegelApi();
  const { $paymentMethods, $logger } = useNuxtApp();
  const { reportPaymentError } = usePaymentError();
  const applePayApi = useKegelApplePayApi();
  const { openError } = useAllModals();
  const { setLoader } = useDialogStore();
  const { roundNumber, gatewayType } = useHelpers();
  const amount = ref<number | null>(null);
  const roundedAmount = ref<number | null>(null);
  const chargebeeId = ref<string>('');
  const isUpsale = ref(false);
  const { track } = useEventsDispatcher();
  const { STRIPE_ID } = useRuntimeConfig().public;
  const { locale } = useI18n();
  const configsStore = usePaymentConfigs(locale);
  const gatewayStore = useKegelGatewayStore();
  const startTime = ref<number | null>(null);
  const authorizationEndTime = ref<number | null>(null);
  const paymentAcceptEndTime = ref<number | null>(null);

  const session = ref<any>(null);
  const instance = ref<any>(null);
  const disableButton = ref(false);
  const isLoading = ref(false);
  const { initUser } = useEmail();
  const { buyUpsale } = payments();

  function createSession(totalPrice: number, roundedPrice: number, id: string, upsale: boolean) {
    startTime.value = performance.now();
    amount.value = totalPrice;
    roundedAmount.value = roundedPrice;
    chargebeeId.value = id;
    isUpsale.value = upsale;
    const sessionParams: SessionParamsModel = {
      countryCode: 'US',
      currencyCode: configsStore.currentConfig[1].currencyCode,
      merchantCapabilities: [
        'supports3DS',
      ],
      supportedNetworks: [
        'visa',
        'masterCard',
        'amex',
        'discover',
      ],
      total: {
        label: 'Dr. Kegel',
        type: 'final',
        amount: totalPrice,
      },
      web_user_uuid: store.userUuid,
      requiredShippingContactFields: [],
    };

    if (requestPayerEmail) {
      sessionParams.requiredShippingContactFields.push('email');
    }

    setLoader(true);
    isLoading.value = true;
    try {
      session.value = new (globalThis as any).ApplePaySession(3, sessionParams);

      session.value.onvalidatemerchant = onvalidatemerchant;
      session.value.onpaymentmethodselected = onpaymentmethodselected;
      session.value.onshippingmethodselected = onshippingmethodselected;
      session.value.onshippingcontactselected = onshippingcontactselected;
      session.value.oncouponcodechanged = oncouponcodechanged;
      session.value.onpaymentauthorized = onpaymentauthorized;
      session.value.oncancel = oncancelcheckout;

      session.value.begin();

      setTimeout(() => {
        disableButton.value = false;
      }, 1000);
    } catch (error: unknown) {
      $logger.error(error as Error, {
        tags: {
          flow: 'payment',
          method: 'applePay',
          step: 'create_session',
        },
        extra: {
          amount: totalPrice,
          chargebeeId: id,
          isUpsale: upsale,
          requestPayerEmail,
        },
      });
      setLoader(false);
      isLoading.value = false;
    }
  }

  function onvalidatemerchant(): void {
    const sessionData = instance.value.session;
    const merchantSession = JSON.parse(sessionData);
    session.value.completeMerchantValidation(merchantSession);
  }

  function onpaymentmethodselected(): void {
    const update = {
      newTotal: {
        label: 'Dr. Kegel',
        type: 'final',
        amount: amount.value,
      },
    };
    session.value.completePaymentMethodSelection(update);
    track('applepay_payment_method_selected');
    disableButton.value = false;
  }

  function onshippingmethodselected(): void {
    const update = {
      newTotal: {
        label: 'Dr. Kegel',
        type: 'final',
        amount: amount.value,
      },
    };
    session.value.completeShippingMethodSelection(update);
  }

  function onshippingcontactselected(): void {
    const update = {};
    session.value.completeShippingContactSelection(update);
  }

  function oncouponcodechanged(event: any) {
    // Define ApplePayCouponCodeUpdate
    const newTotal = (globalThis as any).calculateNewTotal(event.couponCode);
    const newLineItems = (globalThis as any).calculateNewLineItems(event.couponCode);
    const newShippingMethods = (globalThis as any).calculateNewShippingMethods(event.couponCode);
    const errors = (globalThis as any).calculateErrors(event.couponCode);

    session.value.completeCouponCodeChange({
      newTotal,
      newLineItems,
      newShippingMethods,
      errors,
    });
  }

  function oncancelcheckout(): void {
    isLoading.value = false;
    setLoader(false);
    track('applepay_syscheeckout_closed');
  }

  function trackApplePayDuration(result: 'success' | 'error') {
    if (startTime.value === null) {
      return;
    }

    const finalTime = (performance.now() - startTime.value) / 1000;

    track('Payment duration', {
      type: $paymentMethods.applePay,
      gateway: gatewayType.value,
      authorization_duration: roundNumber(authorizationEndTime.value),
      backend_payment_duration: roundNumber(paymentAcceptEndTime.value),
      total_duration: roundNumber(finalTime),
      result,
    });
  }

  function reportAuthorizationRefusal(payerEmail?: string) {
    $logger.note({
      category: 'payment',
      message: payerEmail ? 'Apple Pay user authorization failed' : 'Apple Pay returned no email address',
      level: 'warning',
    });
  }

  async function acceptApplePayPayment(stripeTempToken: string) {
    if (isUpsale.value) {
      return buyUpsale({
        stripe_temp_token: stripeTempToken,
        item: chargebeeId.value,
        web_user_uuid: store.userUuid,
        stage: 'upsale',
        amount: roundedAmount.value,
      });
    }

    return acceptPayment({
      stripe_temp_token: stripeTempToken,
      item: chargebeeId.value,
      raw_stripe_apple_pay: true,
      web_user_uuid: store.userUuid,
      gateway_id: gatewayStore.gid || STRIPE_ID,
    });
  }

  function completeAcceptedPayment(result: ApplePayResult) {
    trackApplePayDuration('success');
    track('applepay_auth_success');
    track('Authorization_Success', {
      type: $paymentMethods.applePay,
      price: roundedAmount.value ?? undefined,
    });
    session.value.completePayment(result);
    successCallBack();
  }

  function completeRejectedPayment(result: ApplePayResult, error: ApiErrorModel) {
    result.status = (globalThis as any).ApplePaySession.STATUS_FAILURE;
    track('applepay_auth_error');
    reportPaymentError({
      error,
      stage: 'Subscription',
      type: $paymentMethods.applePay,
    });
    session.value.completePayment(result);
    trackApplePayDuration(error.status === 409 ? 'success' : 'error');

    if (error.status === 409) {
      onAction();
    }
  }

  function completeUntokenizedPayment(result: ApplePayResult) {
    result.status = (globalThis as any).ApplePaySession.STATUS_FAILURE;
    session.value.completePayment(result);
    trackApplePayDuration('error');
  }

  async function onpaymentauthorized(event: any) {
    const authorizationStartTime = performance.now();
    const { STRIPE_KEY } = useRuntimeConfig().public;
    const payerEmail = event.payment?.shippingContact?.emailAddress;
    const userIsAuthorized = await authorizeUser(payerEmail);

    if (!userIsAuthorized && requestPayerEmail) {
      openError({ content: 'lp_v7_pop_up_payment_error_message' });
      setLoader(false);
      reportAuthorizationRefusal(payerEmail);

      return;
    }

    disableButton.value = false;
    track('applepay_onpaymentauthorized_call');
    track('Authorization_Start', {
      type: $paymentMethods.applePay,
      gateway: gatewayType.value,
    });

    const result: ApplePayResult = {
      status: (globalThis as any).ApplePaySession.STATUS_SUCCESS,
    };

    const { token } = event.payment;

    const data = await sendTokens({
      pk_token: JSON.stringify(token.paymentData),
      pk_token_transaction_id: token.transactionIdentifier,
      pk_token_payment_network: token.paymentMethod.network,
      pk_token_instrument_name: token.paymentMethod.displayName,
      key: gatewayStore.spk || STRIPE_KEY,
    });

    authorizationEndTime.value = (performance.now() - authorizationStartTime) / 1000;

    if (!data) {
      completeUntokenizedPayment(result);
      setLoader(false);
      isLoading.value = false;

      return;
    }

    const paymentAcceptStartTime = performance.now();
    paymentAcceptEndTime.value = (performance.now() - paymentAcceptStartTime) / 1000;

    const error = await acceptApplePayPayment(data.id);

    if (error) {
      completeRejectedPayment(result, error);
    } else {
      completeAcceptedPayment(result);
    }

    setLoader(false);
    isLoading.value = false;
  }

  async function sendTokens(tokens: ApplePayTokensModel) {
    try {
      return await applePayApi.sendTokens(tokens);
    } catch (error: unknown) {
      $logger.error(error as Error, {
        tags: {
          flow: 'payment',
          method: 'applePay',
          step: 'send_tokens',
        },
      });
      openError({ content: 'lp_v7_pop_up_payment_error_message' });
      return false;
    }
  }

  async function acceptPayment(payload: ApplePayAccept) {
    try {
      const result = await API.creditCardAccept(payload);

      return result?.detail?.error;
    } catch (error: unknown) {
      return error as ApiErrorModel;
    }
  }

  async function authorizeUser(email: string): Promise<boolean> {
    if (!requestPayerEmail) {
      return true;
    }

    if (!email) {
      sendEmailRetrievedEvent(false);
      return false;
    }

    const result = await initUser(false, email);
    sendEmailRetrievedEvent(result);

    return result;
  }

  function sendEmailRetrievedEvent(emailAttached: boolean): void {
    track('Payment Email Retrieved', {
      payment_method: $paymentMethods?.applePay,
      email_attached: emailAttached,
    });
  }

  return { isLoading, session, instance, createSession, disableButton };
}
